'use client';

/**
 * DemoAskTranscriptCard — mode8's stacked conversation transcript.
 *
 * Owns the chat-shaped render for a multi-turn ki_ask session. Reads
 * `useDemoAskStore.turns` and stacks one turn-block per entry, oldest
 * on top. Each turn-block:
 *
 *   1. Query line — what the user asked
 *   2. AgentNarrativeCard — agent's DECIDE (summary + recs + audit)
 *   3. Data card — the picked capability's renderer_card, when one is
 *      registered (project_health → ObeyaHealthCard, etc.)
 *   4. Reagraph fallback — when no renderer card matches but an entity
 *      was resolved
 *
 * Per-turn pinning is the load-bearing detail: each turn-block wraps
 * its subtree in `<TurnContext.Provider value={turn}>` so the mounted
 * AgentNarrativeCard + SynthesisCard read THAT turn's response, not
 * the latest store slot. Without this, turn 1's card would morph into
 * turn 2's data on a different-entity follow-up.
 *
 * The renderer slot stays thin — dispatches between this card, the
 * test-run composition, and the empty state.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useDemoAskStore } from '@/lib/stores/demo-ask-store';
import { TurnContext, type TurnContextValue } from '@/lib/stores/turn-context';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import { CANVAS_CARD_REGISTRY } from '../card-registry';
import { CardErrorBoundary } from '../card-error-boundary';
import { AgentNarrativeCard } from '../obeya/agent-narrative-card';


/** Single turn — query chip, narrative, optional data card, optional
 *  reagraph fallback. Mounted inside a TurnContext.Provider so child
 *  cards see this turn's response, not the store-latest. */
function TurnBlock({ turn }: { turn: TurnContextValue }) {
  const T = useCardTypography();

  const { response, rendererCard } = turn;
  const hasNarrative =
    !!response.narrative ||
    !!(response.payload as { narrative?: unknown } | null | undefined)?.narrative;
  const Mount = rendererCard ? CANVAS_CARD_REGISTRY[rendererCard] : undefined;
  // Reagraph fallback fires when the picked capability has no renderer
  // card (or the named card isn't registered) but we still have a
  // resolved entity to anchor a graph view around.
  const canFallbackToGraph =
    !Mount && !!response.entity_sid && !!response.intent?.entity_type;
  const FallbackGraph = canFallbackToGraph
    ? CANVAS_CARD_REGISTRY['agent_graph']
    : undefined;

  return (
    <div className="space-y-2">
      {/* Query line — small chat-style attribution */}
      {turn.query && (
        <div className="flex items-baseline gap-2">
          <span className={cn(T.metadata, 'text-muted-foreground')}>You:</span>
          <span className={cn(T.body, 'text-foreground/90')}>{turn.query}</span>
        </div>
      )}
      {hasNarrative && <AgentNarrativeCard />}
      {/* Self-styled cards (SynthesisCard etc.) mount naked — they carry
          their own border/chrome. The fixed-height wrapper that lived
          here previously forced a 720px container which dwarfed the
          card's typical content (urgency badge + 1-2 recs). */}
      {Mount && (
        <CardErrorBoundary contentKey={rendererCard ?? undefined}>
          <Mount />
        </CardErrorBoundary>
      )}
      {/* Reagraph needs a sized container — it renders a canvas that
          fills its parent. Other cards size themselves. The boundary
          contains reagraph's own mount-time throws (e.g. camera-controls
          null) so a fallback graph never takes down the transcript. */}
      {!Mount && FallbackGraph && (
        <div className="rounded-2xl border border-border bg-card/30 overflow-hidden h-[480px]">
          <CardErrorBoundary contentKey="agent_graph">
            <FallbackGraph />
          </CardErrorBoundary>
        </div>
      )}
    </div>
  );
}


export function DemoAskTranscriptCard() {
  const turns = useDemoAskStore((s) => s.turns);
  if (turns.length === 0) return null;
  return (
    <div className="w-full space-y-6">
      {turns.map((turn) => (
        <TurnContext.Provider key={turn.timestamp} value={turn}>
          <TurnBlock turn={turn} />
        </TurnContext.Provider>
      ))}
    </div>
  );
}
